import React, { useState } from 'react';
import { toLocalISOString } from '../../utils/dateUtils';
import RetroCalendarPopup from './RetroCalendarPopup';
import RetroTimePicker from './RetroTimePicker';
import './RetroDateTimePicker.css';

function RetroDateTimePicker({
  id,
  name,
  value, // ISO string or Date object
  onChange,
  required = false,
  disabled = false,
  label,
  error,
  hint,
  className = '',
  ...props
}) {
  const [showCalendar, setShowCalendar] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);

  // Convert value to date and time strings
  const dateTimeValue = value ? new Date(value) : null;

  // Format date for display (DD.MM.YYYY)
  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
  };

  // Format time for display (HH:MM)
  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const dateDisplay = dateTimeValue ? formatDate(dateTimeValue) : '';
  const timeDisplay = dateTimeValue ? formatTime(dateTimeValue) : '';

  const handleDateSelect = (date) => {
    // Preserve time if it exists, otherwise use noon
    const hours = dateTimeValue ? dateTimeValue.getHours() : 12;
    const minutes = dateTimeValue ? dateTimeValue.getMinutes() : 0;

    const newDateTime = new Date(date);
    newDateTime.setHours(hours);
    newDateTime.setMinutes(minutes);
    newDateTime.setSeconds(0);
    newDateTime.setMilliseconds(0);

    onChange({ target: { name, value: toLocalISOString(newDateTime) } });
    setShowCalendar(false);
  };

  const handleTimeSelect = (date) => {
    // Use the selected time with the existing date, or today's date if none
    const baseDate = dateTimeValue || new Date();
    const newDateTime = new Date(baseDate);
    newDateTime.setHours(date.getHours());
    newDateTime.setMinutes(date.getMinutes());
    newDateTime.setSeconds(0);
    newDateTime.setMilliseconds(0);

    onChange({ target: { name, value: toLocalISOString(newDateTime) } });
    setShowTimePicker(false);
  };

  return (
    <div className={`retro-datetime-picker-wrapper ${className}`}>
      {label && (
        <label className="retro-datetime-label">
          {label}
          {required && <span className="retro-datetime-required"> *</span>}
        </label>
      )}

      <div className="retro-datetime-inputs">
        <div className="retro-datetime-input-group">
          <label className="retro-datetime-sublabel">Datum</label>
          <div className="retro-datetime-input-with-button">
            <input
              type="text"
              value={dateDisplay}
              readOnly
              placeholder="TT.MM.JJJJ"
              required={required}
              disabled={disabled}
              className={`retro-datetime-input ${error ? 'retro-datetime-error' : ''}`}
              onClick={() => !disabled && setShowCalendar(true)}
            />
            <button
              type="button"
              onClick={() => !disabled && setShowCalendar(true)}
              disabled={disabled}
              className="retro-datetime-picker-btn"
            >
              [#]
            </button>
          </div>
        </div>

        <div className="retro-datetime-input-group">
          <label className="retro-datetime-sublabel">Zeit</label>
          <div className="retro-datetime-input-with-button">
            <input
              type="text"
              value={timeDisplay}
              readOnly
              placeholder="HH:MM"
              required={required}
              disabled={disabled}
              className={`retro-datetime-input ${error ? 'retro-datetime-error' : ''}`}
              onClick={() => !disabled && setShowTimePicker(true)}
            />
            <button
              type="button"
              onClick={() => !disabled && setShowTimePicker(true)}
              disabled={disabled}
              className="retro-datetime-picker-btn"
            >
              [T]
            </button>
          </div>
        </div>
      </div>

      {error && <div className="retro-datetime-error-message">{error}</div>}
      {hint && !error && <div className="retro-datetime-hint">{hint}</div>}

      {/* Calendar popup */}
      {showCalendar && (
        <RetroCalendarPopup
          value={value}
          onSelect={handleDateSelect}
          onClose={() => setShowCalendar(false)}
        />
      )}

      {/* Time picker popup */}
      {showTimePicker && (
        <RetroTimePicker
          value={value}
          onSelect={handleTimeSelect}
          onClose={() => setShowTimePicker(false)}
        />
      )}
    </div>
  );
}

export default RetroDateTimePicker;
